import React from "react";
import Layout from "@/components/layout/Layout";
import { useFinancePermissions } from "@/components/finance/banking/update_account_balance/useFinancePermissions";
import { useBankAccounts } from "@/hooks/use-bank-accounts";

import { ReconciliationHeader } from "@/components/finance/banking/reconciliation/ReconciliationHeader";
import {
  ReconciliationFilters,
  type ReconciliationFiltersState,
} from "@/components/finance/banking/reconciliation/ReconciliationFilters";
import { ReconciliationMatchingTabs } from "@/components/finance/banking/reconciliation/ReconciliationMatchingTabs";
import { ReconciliationSummary } from "@/components/finance/banking/reconciliation/ReconciliationSummary";

import { ReconciliationDailyMatrixTable } from "@/components/finance/banking/reconciliation/table-data/DataTable";

import { useLocation, useNavigate } from "react-router-dom";

const DEFAULTS: ReconciliationFiltersState = {
  selectedAccountId: null,
  dateFrom: "2026-02-01",
  dateTo: "2026-02-25",
};

function parseFilters(search: string): ReconciliationFiltersState {
  const sp = new URLSearchParams(search);

  const dateFrom = sp.get("dateFrom") ?? DEFAULTS.dateFrom;
  const dateTo = sp.get("dateTo") ?? DEFAULTS.dateTo;

  const accountIdRaw = sp.get("accountId");
  const selectedAccountId =
    accountIdRaw && accountIdRaw.trim() !== "" ? accountIdRaw : null;


  return { selectedAccountId, dateFrom, dateTo };
}

function buildSearch(next: ReconciliationFiltersState, prevSearch: string): string {
  const sp = new URLSearchParams(prevSearch);

  // Update/clear known filter keys
  if (next.selectedAccountId) sp.set("accountId", next.selectedAccountId);
  else sp.delete("accountId");

  if (next.dateFrom !== DEFAULTS.dateFrom) sp.set("dateFrom", next.dateFrom);
  else sp.delete("dateFrom");

  if (next.dateTo !== DEFAULTS.dateTo) sp.set("dateTo", next.dateTo);
  else sp.delete("dateTo");

  const s = sp.toString();
  return s ? `?${s}` : "";
}

function sameFilters(a: ReconciliationFiltersState, b: ReconciliationFiltersState) {
  return (
    a.selectedAccountId === b.selectedAccountId &&
    a.dateFrom === b.dateFrom &&
    a.dateTo === b.dateTo
  );
}

export default function ReconciliationPage() {
  const { role, isLoading: roleLoading } = useFinancePermissions();
  const canView = role === "finance" || role === "admin";

  const { data: accounts = [], isLoading: accountsLoading, error: accountsError } =
    useBankAccounts(true);

  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = React.useState<ReconciliationFiltersState>(() =>
    parseFilters(location.search)
  );

  React.useEffect(() => {
    const fromUrl = parseFilters(location.search);
    setFilters((prev) => (sameFilters(prev, fromUrl) ? prev : fromUrl));
  }, [location.search]);

  const setFiltersAndSyncUrl = React.useCallback(
    (updater: React.SetStateAction<ReconciliationFiltersState>) => {
      setFilters((prev) => {
        const next =
          typeof updater === "function" ? (updater as any)(prev) : updater;

        const nextSearch = buildSearch(next, location.search);
        if (nextSearch !== location.search) {
          navigate(
            { pathname: location.pathname, search: nextSearch },
            { replace: true }
          );
        }

        return next;
      });
    },
    [navigate, location.pathname, location.search]
  );

  if (roleLoading) {
    return (
      <Layout>
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      </Layout>
    );
  }

  if (!canView) {
    return (
      <Layout>
        <div className="p-6">
          <h1 className="text-lg font-semibold">Reconciliation</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            You don’t have access to bank reconciliation. Ask an admin to grant you Finance role.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <ReconciliationHeader />

        <ReconciliationFilters
          value={filters}
          onChange={setFiltersAndSyncUrl}
          accounts={accounts}
          isAccountsLoading={accountsLoading}
          accountsError={accountsError instanceof Error ? accountsError.message : null}
        />

        <ReconciliationDailyMatrixTable
          onlyGaps={false}
          accounts={accounts as any}
          selectedAccountId={filters.selectedAccountId}
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
        />

        <ReconciliationSummary
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          bankAccountId={filters.selectedAccountId}
          accounts={accounts}
        />

        {/* оставляем в конце как просил */}
        <div id="reconciliation-matching-tabs" className="scroll-mt-4">
          <ReconciliationMatchingTabs
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            bankAccountId={filters.selectedAccountId}
            accounts={accounts}
          />
        </div>
      </div>
    </Layout>
  );
}